/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import { useControlledState } from '@react-stately/utils';
import * as stylex from '@stylexjs/stylex';
import type { HTMLAttributes } from 'react';
import { type ForwardedRef, forwardRef, useContext, useRef } from 'react';
import { DEFAULT_SLOT, Provider } from 'react-aria-components';
import { CloseStrokeIcon } from '../../icons/close-stroke/close-stroke.icon';
import { FullScreenStrokeIcon } from '../../icons/full-screen-stroke/full-screen-stroke.icon';
import { ChatWindowStateContext } from '../chat-window/chat-window.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { NumberBadgeContext } from '../number-badge/number-badge.component';
import { SearchFieldContext } from '../search-field/search-field.component';
import { TextButtonContext } from '../text-button/text-button.component';
import { TextContext } from '../text/text.component';
import { ViewContext } from '../view/view.component';

interface ChatHeaderRenderProps {
  isOpen: boolean;
  isSearch: boolean;
}

interface ChatHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'style' | 'className' | 'children'> {
  style?: stylex.StyleXStyles;
  children?: React.ReactNode | ((renderProps: ChatHeaderRenderProps) => React.ReactNode);
  /** Controlled search-mode state. */
  isSearch?: boolean;
  /** Called when search mode changes (opened or closed internally). */
  onIsSearchChange?: (isSearch: boolean) => void;
}

const ChatHeader = forwardRef((props: ChatHeaderProps, ref: ForwardedRef<HTMLElement>) => {
  const { style, children, isSearch: isSearchProp, onIsSearchChange, ...coreProps } = props;
  const [isSearch, setIsSearch] = useControlledState(isSearchProp, false, onIsSearchChange ?? (() => void 0));
  const { expanded, setExpanded } = useContext(ChatWindowStateContext);

  const searchIconButtonRef = useRef<HTMLButtonElement | null>(null);

  const resolvedChildren = typeof children === 'function' ? children({ isOpen: expanded, isSearch }) : children;

  return (
    <header
      ref={ref}
      {...stylex.props(styles.base, !expanded && styles.collapsed, style)}
      {...coreProps}
      onKeyDown={(e) => {
        if (e.key === 'Escape' && isSearch) {
          setIsSearch(false);
          requestAnimationFrame(() => searchIconButtonRef.current?.focus());
          e.stopPropagation();
        }
      }}>
      <Provider
        values={[
          [
            ViewContext,
            {
              style: viewStyles.base,
            },
          ],
          [
            TextContext,
            {
              size: 14,
              weight: 'regular',
              color: 'primary',
              style: textStyles.base,
            },
          ],
          [
            NumberBadgeContext,
            {
              size: 20,
              color: 'critical',
            },
          ],
          [
            SearchFieldContext,
            {
              size: 32,
              isRounded: true,
              'aria-label': "Search messages",
              autoFocus: true,
              style: searchFieldStyles.base,
            },
          ],
          [
            TextButtonContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  size: 40,
                },
                [CHAT_HEADER_SLOTS.CANCEL_SEARCH_TEXT_BUTTON]: {
                  size: 40,
                  onPress: () => setIsSearch(false),
                },
              },
            },
          ],
          [
            IconButtonContext,
            {
              slots: {
                [DEFAULT_SLOT]: {
                  variant: 'ghost',
                  isRounded: true,
                  size: 32,
                },
                [CHAT_HEADER_SLOTS.SEARCH_ICON_BUTTON]: {
                  variant: 'ghost',
                  isRounded: true,
                  size: 32,
                  'aria-label': 'Search',
                  hidden: !expanded,
                  ref: searchIconButtonRef,
                  onPress: () => setIsSearch(true),
                },
                [CHAT_HEADER_SLOTS.TOGGLE_WINDOW_ICON_BUTTON]: {
                  variant: 'ghost',
                  isRounded: true,
                  size: 32,
                  'aria-label': 'Toggle chat window',
                  children: expanded ? <CloseStrokeIcon /> : <FullScreenStrokeIcon />,
                  onPress: () => {
                    setIsSearch(false);
                    setExpanded(!expanded);
                  },
                },
              },
            },
          ],
        ]}>
        {resolvedChildren}
      </Provider>
    </header>
  );
});

const styles = stylex.create({
  base: {
    gap: 8,
    paddingInline: 16,
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    minHeight: 44,
    paddingTop: 12,
  },
  collapsed: {
    minHeight: 56,
    paddingBottom: 12,
  }
});

const textStyles = stylex.create({
  base: {
    textWrap: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  }
});

const viewStyles = stylex.create({
  base: {
    gap: 8,
    alignItems: 'center',
    display: 'flex',
  },
});

const searchFieldStyles = stylex.create({
  base: {
    width: '100%',
  },
});

const CHAT_HEADER_SLOTS = {
  CANCEL_SEARCH_TEXT_BUTTON: 'cancel-search-text-button',
  SEARCH_ICON_BUTTON: 'search-icon-button',
  TOGGLE_WINDOW_ICON_BUTTON: 'toggle-window-icon-button',
};

export { CHAT_HEADER_SLOTS, ChatHeader, type ChatHeaderProps, type ChatHeaderRenderProps };
